import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, PhoneCall, Calendar } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import useReveal from "../lib/useReveal";
import { CALENDLY_URL, CONTACT_EMAIL } from "../lib/constants";

const CALENDLY = CALENDLY_URL;

export default function Contact() {
  useReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.title = "Contact | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Get in touch with RevGen. Book a free 30-minute back office assessment to see which plan or project fits your business."
      );
    }
  }, []);

  return (
    <div data-testid="contact-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <section
          data-testid="contact-hero"
          className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden"
        >
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-32 -right-32 h-[460px] w-[460px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
            <div className="absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
          </div>

          <div className="mx-auto max-w-[1100px] px-6 lg:px-8 reveal">
            <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
              <span className="eyebrow text-[#16A06A]">Contact</span>
            </span>
            <h1 className="h1-hero mt-5 text-[#0A1628]">
              Get the admin{" "}
              <span className="text-[#16A06A]">off your desk.</span>
            </h1>
            <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-2xl">
              Book a free 30-minute back office assessment. We'll score where your
              time is going and tell you which plan fits — or if a project is
              the smarter start.
            </p>

            <div className="mt-8">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-hero-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all"
              >
                Get a Free Back Office Assessment
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-[#F8FAFC]">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-8 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Calendar,
                title: "Book a call",
                body:
                  "The fastest way to reach us. 30 minutes, video or phone — your choice.",
                cta: { label: "Open scheduler", href: CALENDLY, external: true },
              },
              {
                icon: Mail,
                title: "Email",
                body: "Prefer async? Drop us a note and we'll reply within one business day.",
                cta: { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
              },
              {
                icon: PhoneCall,
                title: "Not sure where to start?",
                body:
                  "Tell us your revenue and biggest bottleneck on the call — we'll point you to the right plan or project.",
                cta: { label: "See plans", href: "/pricing" },
              },
            ].map((c, i) => {
              const Icon = c.icon;
              const cta = c.cta;
              return (
                <article
                  key={c.title}
                  data-testid={`contact-card-${i + 1}`}
                  className="reveal bg-white rounded-2xl border border-slate-200 card-soft p-7 hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A]">
                    <Icon size={18} />
                  </div>
                  <h3 className="mt-5 text-[18px] font-semibold text-[#0A1628]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-slate-500">
                    {c.body}
                  </p>
                  {cta.external ? (
                    <a
                      href={cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#16A06A] hover:text-[#0E7C53]"
                    >
                      {cta.label}
                      <ArrowRight size={14} />
                    </a>
                  ) : cta.href.startsWith("mailto:") ? (
                    <a
                      href={cta.href}
                      className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#16A06A] hover:text-[#0E7C53] break-all"
                    >
                      {cta.label}
                    </a>
                  ) : (
                    <Link
                      to={cta.href}
                      className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#16A06A] hover:text-[#0E7C53]"
                    >
                      {cta.label}
                      <ArrowRight size={14} />
                    </Link>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
